import { useId } from "react";

/**
 * The shared look every chart in the app draws from: a gradient fill per colour,
 * a drop shadow, and a glow.
 *
 * Each series gets a gradient of its own colour rather than a second hue — lighter
 * at the near edge, full strength at the far one — so a mark reads as a lit
 * surface instead of flat paint, without changing what colour identifies it. The
 * stops are opacities of whatever colour is passed in, so a caller using CSS
 * variables keeps its dark-mode steps instead of inheriting the light ones.
 *
 * The depth is lighting, not perspective. Marks are still measured from a common
 * baseline at their true size; tilting a plot into a real 3D projection changes
 * how big a mark looks depending on where it sits, which is the one thing that
 * would make the numbers wrong.
 */
export interface ChartFxIds {
  /** Gradient url() for the series at `index`; falls back to the flat colour. */
  fill: (index: number) => string;
  /** filter url() for the soft drop shadow. */
  shadow: string;
  /** filter url() for the coloured bloom. */
  glow: string;
}

/** Stable ids for one chart instance, so two charts on a page never collide. */
export function useChartFx(colors: string[]): ChartFxIds {
  const raw = useId();
  // useId returns a value containing colons, which are not valid in a url(#id).
  const prefix = `fx${raw.replace(/[^a-zA-Z0-9]/g, "")}`;

  return {
    fill: (index: number) =>
      colors[index] ? `url(#${prefix}-g${index})` : "none",
    shadow: `url(#${prefix}-shadow)`,
    glow: `url(#${prefix}-glow)`,
    // The defs element itself is rendered by <ChartFxDefs> with the same ids.
    ...({ prefix } as object),
  } as ChartFxIds & { prefix: string };
}
